<script lang="ts">
	import type { SbImage, SbTextColor, TextAlignment } from '../types';
	import PageGrid from '../components/pageGrid.svelte';
	import Image from '../components/image.svelte';
	import SbRichtext from '../components/sbRichtext.svelte';
	import { editable } from '../editable';
	import type { ISbRichtext, ISbStoryData } from '@storyblok/js';
	import Blok from '../blok.svelte';

	interface Props {
		content: ISbStoryData<{
			title: string | ISbRichtext;
			text: string | ISbRichtext;
			image?: SbImage;
			asset: Array<ISbStoryData>;
			text_align: TextAlignment;
			text_color: SbTextColor;
			button: Array<ISbStoryData>;
			align: 'imageRight' | 'imageLeft';
			title_visibility: 'default' | 'mobile_only' | 'desktop_only';
			title_position: 'below_image' | 'above_image';
		}>['content'];
	}

	let { content }: Props = $props();

	let titlePosition = $derived(content.title_position || 'below_image');
</script>

<div use:editable={content}>
	<PageGrid gridClass="grid-flow-row-dense max-lg:gap-y-4 text-{content.text_color || 'brown-1'}">
		<div
			class={[
				'max-lg:col-span-full lg:hidden',
				{
					hidden: titlePosition === 'below_image'
				}
			]}
		>
			<div
				class={[
					'text-[32px]/[110%] font-semibold tracking-[-.32px] lg:text-[48px]/[110%] lg:tracking-[-.48px]',
					{
						'lg:hidden': content.title_visibility === 'mobile_only',
						'max-lg:hidden': content.title_visibility === 'desktop_only'
					}
				]}
			>
				<SbRichtext text={content.title} />
			</div>
		</div>
		<div
			class={[
				'max-lg:col-span-full',
				{
					'lg:col-start-7 lg:col-end-13': content.align === 'imageRight',
					'lg:col-start-1 lg:col-end-7': content.align === 'imageLeft'
				}
			]}
		>
			{#if content.asset?.length > 0}
				<div class="aspect-4/3 overflow-hidden rounded-2xl">
					<Blok content={content.asset[0]} />
				</div>
			{:else if content.image?.filename}
				<Image
					class="aspect-4/3 rounded-2xl object-cover"
					src={content.image.filename}
					title={content.image.title ?? ''}
					alt={content.image.alt ?? ''}
					width={950}
				/>
			{/if}
		</div>
		<div
			class={[
				'flex flex-col justify-center max-lg:col-span-full max-lg:gap-y-4',
				{
					'lg:col-start-1 lg:col-end-6': content.align === 'imageRight',
					'lg:col-start-8 lg:col-end-13': content.align === 'imageLeft',
					'items-center text-center': content.text_align === 'center'
				}
			]}
		>
			<div
				class={[
					'text-[32px]/[110%] font-semibold tracking-[-.32px] lg:text-[48px]/[110%] lg:tracking-[-.48px]',
					{
						'lg:hidden': content.title_visibility === 'mobile_only',
						'max-lg:hidden':
							content.title_visibility === 'desktop_only' || titlePosition === 'above_image'
					}
				]}
			>
				<SbRichtext text={content.title} />
			</div>
			<div class="text-[18px]/[140%] lg:mt-7 lg:text-[20px]">
				<SbRichtext text={content.text} />
			</div>

			{#each content.button as button}
				<div class="flex lg:mt-8">
					<Blok content={button} />
				</div>
			{/each}
		</div>
	</PageGrid>
</div>
